import { createClient, createPreviewSubscriptionHook } from "next-sanity";
import createImageUrlBuilder from "@sanity/image-url";

const config = {
  dataset: process.env.NEXT_PUBLIC_SANITY_DATASET,
  projectId: process.env.NEXT_PUBLIC_SANITY_PROJECT_ID,
  useCdn: process.env.NODE_ENV !== "production",
  // useCdn == true gives fast, cheap responses using a globally distributed cache.
  // When in production the Sanity API is only queried on build-time, and on-demand when responding to webhooks.
  // Thus the data need to be fresh and API response time is less important.
  // When in development/working locally, it's more important to keep costs down as hot reloading can incurr a lot of API calls
  // And every page load calls getStaticProps.
  // To get the lowest latency, lowest cost, and latest data, use the Instant Preview mode
  apiVersion: "2022-01-11",
};

export const imageBuilder = createImageUrlBuilder(config);
export const urlForImage = (source) =>
  imageBuilder.image(source).auto("format").fit("max");

export const usePreviewSubscription = createPreviewSubscriptionHook(config);
export const client = createClient(config);
export const previewClient = createClient({
  ...config,
  useCdn: false,
  token: process.env.SANITY_API_TOKEN,
});

export const getClient = (usePreview = false) =>
  usePreview ? previewClient : client;

export default client;

export interface SanityImage {
  _type: "image";
  alt: string;
  asset: { _type: "reference"; _ref: string };
  crop: { _type: "sanity.imageCrop" } & Record<string, number>;
  hotspot: { _type: "sanity.imageHotspot" } & Record<string, number>;
}

interface ImagePaletteSwatch {
  _type: "sanity.imagePaletteSwatch";
  background: string;
  foreground: string;
  population: number;
  title: string;
}

export interface SanityImageWithPalette extends SanityImage {
  palette?: {
    _type: "sanity.imagePalette";
    darkMuted: ImagePaletteSwatch;
    darkVibrant: ImagePaletteSwatch;
    dominant: ImagePaletteSwatch;
    lightMuted: ImagePaletteSwatch;
    lightVibrant: ImagePaletteSwatch;
    muted: ImagePaletteSwatch;
    vibrant: ImagePaletteSwatch;
  };
}
